// =============================================================================
// Location matching — is a booked address one of Mount Sinai's own locations?
//
// WHY THIS EXISTS
// Mount Sinai asked (2026-09-04, via Terrence) for a way to review trips booked
// to or from somewhere that is not an MSMC location. The driver is data quality
// on THEIR side: staff appear to be booking trips that are not patient
// transport, and MSMC want to find them and ask why. It is not a Royal billing
// question — Royal carries whatever is booked — so the output is a review list,
// not an enforcement gate. Nothing here blocks a booking.
//
// THE RULE THAT MATTERS
// "Pickup or dropoff is not an MSMC location" sounds like the test, and is not:
// a discharge is hospital -> the patient's home, an admission is home ->
// hospital. Exactly ONE known endpoint is the shape of a normal patient trip,
// so that rule flags nearly every booking and the list is useless on day one.
//
// The anomaly is a trip that never touches Mount Sinai at all — NEITHER end
// known. That is what "someone booked a car that was not for a patient" looks
// like. `classifyTripEndpoints` therefore reports each end separately and lets
// the caller choose the threshold; see `TripLocationVerdict`.
//
// WHY TEXT AND NOT COORDINATES
// `bookings` carries lat/lon columns, but the TripLink sync inserts them NULL
// (triplinkSync.ts, case 1) — so every V1 and phone booking has no coordinates.
// Those are precisely the bookings that bypass the V2 form's location picker
// and are most likely to be off-location, so a geo test would be blind exactly
// where it is needed. Matching is therefore on the address text, which every
// booking has.
//
// PRECISION OVER RECALL, DELIBERATELY
// A false positive costs a human an investigation into a trip that was fine.
// This list is reviewed by people with other jobs, so a list that cries wolf
// gets ignored and the feature dies. Where a comparison is uncertain we report
// NO match rather than guess, and the number-plus-street rule below is
// deliberately strict.
// =============================================================================

/** The subset of a `locations` row needed to compare an address against it. */
export interface KnownLocation {
  id: string;
  name: string;
  addressLine: string;
  city: string;
  postalCode: string;
  /** Other addresses the same building goes by: a second street number
   *  ("4306" beside "4310 Alton Rd"), or the street's other name ("2845
   *  Aventura Blvd" for "2845 NE 199th St"). Each is one line, ZIP included
   *  when known. Matched by the same strict rules as `addressLine`. */
  otherAddresses?: readonly string[];
  /** The location's map pin. Drizzle hands numeric columns over as strings. */
  lat?: number | string | null;
  lon?: number | string | null;
}

/** Where Google put an address on the map, and how sure it was: `exact`
 *  when it found the building itself (ROOFTOP), `estimate` when it placed the
 *  number along the street (RANGE_INTERPOLATED, GEOMETRIC_CENTER). Anything
 *  vaguer is never passed in. */
export interface GeocodedPoint {
  lat: number;
  lon: number;
  precision: "exact" | "estimate";
}

/** Looks up an address already geocoded, by `geocodeQuery`. Supplied by the
 *  caller so the matcher stays pure: no network, no database. */
export type GeocodeLookup = (query: string) => GeocodedPoint | undefined;

/** One endpoint (pickup or dropoff) compared against the known locations. */
export interface EndpointMatch {
  matched: boolean;
  /** Which location matched. Null when `matched` is false. */
  locationId: string | null;
  locationName: string | null;
  /** `exact`  — street number + street name + postal code all agree.
   *  `strong` — street number + street name agree; postal absent or unknown
   *             on one side, which is the normal case for TMS-synced rows
   *             (they carry a single combined address string and an empty
   *             `city_zip`).
   *  `map`    — the wording differs, but it is the same building number and
   *             Google puts it at the location's pin (see MAP CHECK below).
   *  `none`   — no match. */
  confidence: "exact" | "strong" | "map" | "none";
}

export type TripLocationVerdict =
  /** Both ends are MSMC locations — an internal transfer between campuses or
   *  buildings. Normal. */
  | "both_known"
  /** Exactly one end is an MSMC location. THE NORMAL PATIENT TRIP: hospital to
   *  home, or home to hospital. Flagging these is what makes a review list
   *  useless, so callers should not. */
  | "one_known"
  /** Neither end is an MSMC location — the trip never touches Mount Sinai.
   *  This is the review case. */
  | "neither_known"
  /** At least one endpoint had no usable address text at all, so the trip
   *  could not be judged. Kept separate from `neither_known` so a data gap is
   *  never reported to a reviewer as a suspicious trip. */
  | "indeterminate";

// -----------------------------------------------------------------------------
// Normalisation
// -----------------------------------------------------------------------------

/** Street-type suffixes, mapped to a single canonical token. Addresses arrive
 *  from three channels (V2 form autocomplete, TripLink's feed, and dispatchers
 *  typing by hand), so "Rd" / "Road" / "rd." all reach us for the same street. */
const STREET_SUFFIXES: Record<string, string> = {
  st: "street", street: "street",
  rd: "road", road: "road",
  ave: "avenue", av: "avenue", avenue: "avenue",
  blvd: "boulevard", boulevard: "boulevard",
  dr: "drive", drive: "drive",
  ln: "lane", lane: "lane",
  ct: "court", court: "court",
  pl: "place", place: "place",
  ter: "terrace", terrace: "terrace",
  pkwy: "parkway", parkway: "parkway",
  hwy: "highway", highway: "highway",
  cir: "circle", circle: "circle",
  sq: "square", square: "square",
  way: "way",
};

/** Compass prefixes/suffixes, canonicalised. "N Alton Rd" vs "North Alton Road". */
const DIRECTIONALS: Record<string, string> = {
  n: "north", north: "north",
  s: "south", south: "south",
  e: "east", east: "east",
  w: "west", west: "west",
  ne: "northeast", northeast: "northeast",
  nw: "northwest", northwest: "northwest",
  se: "southeast", southeast: "southeast",
  sw: "southwest", southwest: "southwest",
};

/** Sub-address markers. Everything from one of these onward is dropped: a
 *  suite or room number distinguishes a desk, not a building, and the whole
 *  point is to recognise the building. "4300 Alton Rd Suite 200" and
 *  "4300 Alton Rd" are the same place for this purpose. */
const UNIT_MARKERS = new Set([
  "suite", "ste", "apt", "apartment", "unit", "floor", "fl", "room", "rm",
  "bldg", "building", "dept", "department",
]);

/** A US ZIP in the string, 5 digits or ZIP+4. Returns the 5-digit prefix,
 *  since the +4 varies by desk within one building.
 *
 *  TWO THINGS THIS HAS TO GET RIGHT, both learned from production:
 *
 *  1. A LEADING HOUSE NUMBER IS NOT A ZIP. "10990 Biscayne Blvd, Miami, FL
 *     33161" used to yield "10990" — the first five-digit run in the string.
 *     That fake ZIP was then compared against the location's real "33161",
 *     disagreed, and rejected the match, so every trip pickup at a building
 *     with a five-digit street number was reported to Mount Sinai as a trip
 *     that never touched the hospital. Terrence reported exactly that on
 *     2026-09-10 for Miami Shores. The house number is stripped before the
 *     scan; a bare "33161" (the `postal_code` column) has no trailing text
 *     and so is left alone.
 *
 *  2. THE ZIP IS AT THE END. Taking the LAST match rather than the first
 *     keeps a five-digit unit number ("12345 Main St Apt 67890, Miami FL
 *     33101") from winning over the real ZIP. */
export function extractPostalCode(raw: string | null | undefined): string {
  // Drop a leading house number: digits followed by whitespace and more text.
  // A field that is nothing but a ZIP has no such trailing text and survives.
  const text = String(raw ?? "").replace(/^\s*\d+(?=\s+\S)/u, "");
  let found = "";
  for (const m of text.matchAll(/\b(\d{5})(?:-\d{4})?\b/gu)) found = m[1]!;
  return found;
}

/** A location NAME or a booking address reduced for whole-string comparison:
 *  case-folded, punctuation-flattened, whitespace-collapsed. Used only for the
 *  label path, where the two sides are expected to be the same string rather
 *  than two renderings of one address. */
export function normalizeLabel(raw: string | null | undefined): string {
  return String(raw ?? "")
    .toLowerCase()
    .replace(/[.,#/\\-]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

/** Leading house number. Shares its definition with the sync's `streetNumber`
 *  (triplinkSync.ts) on purpose — both answer "which building is this". */
export function houseNumber(raw: string | null | undefined): string {
  const m = /^\s*(\d+)/u.exec(String(raw ?? ""));
  return m ? m[1]! : "";
}

/**
 * Reduce an address to the tokens that identify a BUILDING, dropping
 * everything that varies between renderings of the same one.
 *
 * Returns the tokens after the house number, with suffixes and directionals
 * canonicalised and any unit/suite tail removed. The city is deliberately NOT
 * included: TMS rows arrive as one combined "Street, City" string while
 * `locations` stores the city separately, so including it would make the two
 * sides structurally incomparable.
 */
/** "12th" -> "12", "152nd" -> "152", "3rd" -> "3". Anchored with leading
 *  digits so the street suffix "Rd" is never mistaken for an ordinal. */
const ORDINAL_SUFFIX = /^(\d+)(?:st|nd|rd|th)$/u;

export function streetTokens(raw: string | null | undefined): string[] {
  const cleaned = String(raw ?? "")
    .toLowerCase()
    // A comma ends the street portion in every format we receive
    // ("4300 Alton Rd, Miami Beach, FL 33140").
    .split(",")[0]!
    // Punctuation is noise: "rd." and "rd" are the same token.
    .replace(/[.#]/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();

  const parts = cleaned.split(" ").filter(Boolean);
  const out: string[] = [];
  let sawStreetName = false;
  for (const rawPart of parts) {
    // Everything from a unit marker onward names a desk, not a building.
    if (UNIT_MARKERS.has(rawPart)) break;

    // "12th" and "12" are the same street. Miami's grid is numbered and the
    // two sides disagree about the ordinal constantly — TMS receives whatever
    // the caller typed, while `locations` holds whatever an admin entered. The
    // pattern needs leading digits, so the street suffix "Rd" is untouched
    // while the ordinal "3rd" is normalised to "3".
    const part = rawPart.replace(ORDINAL_SUFFIX, "$1");

    const isBareNumber = /^\d+$/u.test(part);
    const isDirectional = Object.hasOwn(DIRECTIONALS, part);
    const isSuffix = Object.hasOwn(STREET_SUFFIXES, part);

    // Drop the house number — compared separately and exactly.
    if (out.length === 0 && isBareNumber) continue;

    // A bare number after the street NAME is a unit ("4300 Alton Rd 200").
    // Before it, the number IS the street name.
    //
    // This used to test `out.length > 0`, which is also true once a
    // DIRECTIONAL has been collected — so "1000 NW 12 Ave" dropped "12" as a
    // unit and tokenised to ["northwest", "avenue"], deleting the street name
    // itself. It failed in both directions at once:
    //
    //   * a booking typed "1000 NW 12 Ave" did not match the location
    //     "1000 NW 12th Ave", so a real discharge was reported to Mount Sinai
    //     as a trip that never touched the hospital;
    //   * worse, a LOCATION stored without the ordinal reduced to the core
    //     token ["northwest"], which any address on any NW avenue matched —
    //     with confidence "exact", because the ZIP corroborated it. Genuinely
    //     off-site trips were then silently dropped from the review this
    //     report exists to produce.
    if (sawStreetName && isBareNumber) continue;

    const canonical = DIRECTIONALS[part] ?? STREET_SUFFIXES[part] ?? part;
    out.push(canonical);

    // THE STREET NAME ENDS AT ITS TYPE. Everything after "Rd" / "Ave" /
    // "Drive" is a building, floor, suite, city or state — not part of the
    // street. Without this, a real production address of the shape
    //
    //   "4300 Alton Rd Gumenick Bldg 2nd Fl Ste 2522 Miami Beach FL 33140-2800"
    //
    // (no commas anywhere, so the comma split never fires) tokenised to
    // ["alton", "road", "gumenick"] and failed to match the "4300 Alton Rd"
    // location — flagging a drop-off AT the hospital as a trip that never
    // touched it. The unit-marker break came too late: "gumenick" was already
    // collected before "bldg" was reached.
    //
    // Only stops once a street NAME has been seen, so an address whose name
    // begins with a type word ("4300 Avenue B") is not truncated to its first
    // token.
    if (isSuffix && sawStreetName) break;
    // A directional is a modifier on the name, not the name. Counting it as
    // the name is what let the bare-number rule above eat "12" in
    // "NW 12 Ave", and it would equally have truncated "NW Ave B" at "Ave".
    if (!isSuffix && !isDirectional) sawStreetName = true;
  }
  return out;
}

/** The street's type, canonical ("road" for "Rd"), when the address names one:
 *  "1510 Bay Road" and "1510 Bay Dr" are different streets. The last type
 *  word, so the "St" of "St James Pl" is no type. */
export function streetType(raw: string | null | undefined): string | undefined {
  const suffixes = new Set(Object.values(STREET_SUFFIXES));
  return streetTokens(raw).filter((t) => suffixes.has(t)).at(-1);
}

/** The street tokens minus the type suffix — the part that actually names the
 *  street. Guards the "4300 Alton Road" vs "4300 Alton Avenue" case, where the
 *  house number and the name agree but the streets are different. */
function coreTokens(tokens: string[]): string[] {
  const suffixes = new Set(Object.values(STREET_SUFFIXES));
  return tokens.filter((t) => !suffixes.has(t));
}

/** Whether `matchAddressToLocations` can use this line as a location's
 *  address at all: ONE address, a plain building number and then a street
 *  name. Used to refuse a line when it's saved, rather than let it look saved
 *  while recognising nothing. What it catches is what an admin is likely to
 *  type for a building with two numbers:
 *    "4306", "4306-4310 Alton Rd", "4306 4310 Alton Rd"   (no single number)
 *    "4306 and 4310 Alton Rd", "4306 thru 4310 Alton Rd"   (a range)
 *    "4306 Alton Rd, 4310 Alton Rd", ".../ 4310 Alton Rd"  (two addresses)
 *  Unit text after the street ("Suite 602/603") is left alone: the matcher
 *  stops at the street type anyway. */
export function isMatchableAddress(line: string): boolean {
  if (!/^\s*\d+\s+\D/u.test(line)) return false;
  if (/(?:[,/&;]|\b(?:and|or)\b)\s*\d+\s+\p{L}/iu.test(line)) return false;
  const core = coreTokens(streetTokens(line));
  return (
    core.length > 0 &&
    core.every((t) => /[\p{L}\p{N}]/u.test(t) && !RANGE_WORDS.has(t))
  );
}

const RANGE_WORDS = new Set(["and", "or", "thru", "through", "to", "&"]);

/** The ZIP typed into an other-address line: five digits at the very end,
 *  after a comma or a state ("..., FL 33140", "...FL33140", ZIP+4 too). A
 *  number anywhere else is a suite or an office, not a ZIP, so "4306 Alton
 *  Rd Suite 10200" has none and takes the building's. */
function otherAddressPostal(line: string): string {
  const m = /(?:,|\b[A-Z]{2})\s*(\d{5})(?:-?\d{4})?\s*$/u.exec(line);
  return m ? m[1]! : "";
}

// -----------------------------------------------------------------------------
// Matching
// -----------------------------------------------------------------------------

/**
 * Compare one booked address against every known location.
 *
 * The test is deliberately strict, in this order:
 *   1. House numbers must be present on both sides and EQUAL. Cheap, and the
 *      single highest-signal field in a US address.
 *   2. The street name (suffix removed) must be identical as a token set.
 *      Order-insensitive so "Alton Rd N" and "N Alton Rd" agree; not a
 *      substring test, so "Alton" never matches "Alton Beach".
 *   3. The street TYPE, when both sides state one, must agree — this is what
 *      separates Alton Road from Alton Avenue.
 *   4. Postal code, when both sides have one, must agree. When it does, the
 *      match is `exact`; when either side lacks one, `strong`.
 *
 * Anything short of that is no match. `cityZip` is optional and only ever
 * corroborates — TMS-synced rows store it empty.
 */
export function matchAddressToLocations(
  address: string | null | undefined,
  cityZip: string | null | undefined,
  locations: readonly KnownLocation[],
  lookup?: GeocodeLookup,
): EndpointMatch {
  const miss: EndpointMatch = {
    matched: false,
    locationId: null,
    locationName: null,
    confidence: "none",
  };

  const addrText = String(address ?? "").trim();
  if (!addrText) return miss;

  // ---- Label match, BEFORE the street comparison --------------------------
  // The booking form writes `picked.address || picked.label` into
  // pickupAddress (PickupSection.tsx), so a location with a blank address_line
  // stores its NAME verbatim — e.g. "Hilderbrandt Emergency Center - 4300
  // Alton Road". Six of the sixteen production rows are exactly that shape
  // (child buildings under the main campus), so without this a discharge from
  // the Emergency Department would be reported as a trip that never touched
  // the hospital: a false positive on ordinary patient transport, on the very
  // list whose credibility the whole design depends on.
  //
  // Compared as a whole normalised string, not fuzzily — this recognises a
  // value the picker itself wrote, so an exact correspondence is available and
  // anything looser would reintroduce the guessing the rest of this module
  // refuses to do.
  const addrLabel = normalizeLabel(addrText);
  if (addrLabel) {
    for (const loc of locations) {
      if (normalizeLabel(loc.name) === addrLabel) {
        return {
          matched: true,
          locationId: loc.id,
          locationName: loc.name,
          confidence: "exact",
        };
      }
    }
  }

  const num = houseNumber(addrText);
  // No house number means nothing reliable to anchor on (a place name like
  // "Mount Sinai Medical Center", or a malformed row). Refuse rather than
  // fall back to a fuzzy name comparison that would invent matches.
  if (!num) return miss;

  const tokens = streetTokens(addrText);
  const core = coreTokens(tokens);
  if (core.length === 0) return mapMatch(addrText, cityZip, num, locations, lookup) ?? miss;

  const suffixValues = new Set(Object.values(STREET_SUFFIXES));
  const addrType = tokens.find((t) => suffixValues.has(t)) ?? "";
  // The ZIP may live in either field depending on the write path.
  const addrPostal = extractPostalCode(cityZip) || extractPostalCode(addrText);

  let best: EndpointMatch = miss;

  // A location's own address, then any other address the building goes by.
  // An other address carries its ZIP inside the line when the admin typed
  // one; otherwise it takes the building's, so "2845 Aventura Blvd" saved
  // without a ZIP is still held to 33180 and doesn't also match a 2845
  // Aventura Blvd in another city.
  const candidates = locations.flatMap((loc) => [
    {
      loc,
      line: loc.addressLine,
      postal: extractPostalCode(loc.postalCode) || extractPostalCode(loc.addressLine),
    },
    // JSONB from the database: never trust its shape enough to throw on it,
    // or one bad row would take down the off-site check for every trip.
    ...(Array.isArray(loc.otherAddresses) ? loc.otherAddresses : [])
      .filter((line): line is string => typeof line === "string")
      .map((line) => ({
        loc,
        line,
        postal: otherAddressPostal(line) || extractPostalCode(loc.postalCode),
      })),
  ]);

  for (const { loc, line, postal } of candidates) {
    if (houseNumber(line) !== num) continue;

    const locTokens = streetTokens(line);
    const locCore = coreTokens(locTokens);
    if (locCore.length === 0) continue;
    if (locCore.length !== core.length) continue;
    if (!locCore.every((t, i) => t === core[i])) continue;

    // Both sides naming a street type and disagreeing = different streets.
    const locType = locTokens.find((t) => suffixValues.has(t)) ?? "";
    if (addrType && locType && addrType !== locType) continue;

    const locPostal = postal;
    if (addrPostal && locPostal && addrPostal !== locPostal) continue;

    const confidence: EndpointMatch["confidence"] =
      addrPostal && locPostal && addrPostal === locPostal ? "exact" : "strong";

    // Keep the strongest match; an exact one ends the search.
    if (confidence === "exact") {
      return {
        matched: true,
        locationId: loc.id,
        locationName: loc.name,
        confidence,
      };
    }
    if (!best.matched) {
      best = {
        matched: true,
        locationId: loc.id,
        locationName: loc.name,
        confidence,
      };
    }
  }

  if (best.matched) return best;
  return mapMatch(addrText, cityZip, num, locations, lookup) ?? miss;
}

// -----------------------------------------------------------------------------
// MAP CHECK
//
// The wording of an address is not the building. Production, 2026-09-22: most
// of Mount Sinai's Off-site list was their own buildings written another way —
// "2845 Aventura Blvd" for the "2845 NE 199th St" on the list (one road, two
// names), "836 E Ponce De Leon Blvd", "836 Ponce DeLeon Blvd", "6050 W 20th
// Ct" for W 20th Ave. Phone bookings arrive from dispatch with no coordinates,
// so the text is all we get, and Miami has many roads with two names.
//
// So when the words don't match, ask where the address is. It counts as the
// location only if BOTH hold:
//   * the same building number as the location (its address or one of its
//     other addresses) — so the doctor's office next door, at another number,
//     stays off-site;
//   * Google puts it at the location's pin: within 75 m when Google found the
//     building itself, 300 m when it only estimated a spot along the street.
//     Measured on the live list: the same buildings land 10–12 m away when
//     exact and 239–269 m when estimated; "522 E 26th St", an exact hit on a
//     real building one street from the 522 E 25th St clinic, lands 113 m
//     away and so stays off-site; the nearest genuinely off-site place was
//     2.4 km away.
// -----------------------------------------------------------------------------

const MAP_RADIUS_M = { exact: 75, estimate: 300 } as const;

/** The text sent to Google for an endpoint, which is also its cache key's
 *  source: the street line plus the separate city/ZIP when there is one. */
export function geocodeQuery(
  address: string | null | undefined,
  cityZip: string | null | undefined,
): string {
  return [address, cityZip]
    .map((p) => String(p ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

/** Cache key for a geocode query: case and spacing don't change the answer. */
export function geocodeKey(query: string): string {
  return query.toLowerCase().replace(/\s+/gu, " ").trim();
}

function pinOf(loc: KnownLocation): { lat: number; lon: number } | null {
  const lat = Number(loc.lat), lon = Number(loc.lon);
  if (loc.lat == null || loc.lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat, lon };
}

/** Locations with a pin whose own address, or one of whose other addresses,
 *  carries this building number — with those lines, for the name check. */
function pinnedLocationsNumbered(
  num: string,
  locations: readonly KnownLocation[],
): Array<{ loc: KnownLocation; pin: { lat: number; lon: number }; lines: string[] }> {
  const out: Array<{ loc: KnownLocation; pin: { lat: number; lon: number }; lines: string[] }> = [];
  for (const loc of locations) {
    const pin = pinOf(loc);
    if (!pin) continue;
    const others = Array.isArray(loc.otherAddresses) ? loc.otherAddresses : [];
    const lines = [loc.addressLine, ...others]
      .filter((l): l is string => typeof l === "string")
      .filter((l) => houseNumber(l) === num);
    if (lines.length > 0) out.push({ loc, pin, lines });
  }
  return out;
}

/** The street's own name: its first word after the direction, without the
 *  type. "E 25th St" -> "25", "Ponce de Leon Blvd" -> "ponce". Only the first
 *  word, because a booking with no street type runs on into the city
 *  ("2845 NE 198 Aventura FL") and the city must not pass for the street. */
function streetName(line: string): string {
  const directions = new Set(Object.values(DIRECTIONALS));
  return coreTokens(streetTokens(line)).find((t) => !directions.has(t)) ?? "";
}

function metresBetween(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const h =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lon - a.lon) / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.sqrt(h));
}

function mapMatch(
  addrText: string,
  cityZip: string | null | undefined,
  num: string,
  locations: readonly KnownLocation[],
  lookup: GeocodeLookup | undefined,
): EndpointMatch | null {
  if (!lookup) return null;
  const candidates = pinnedLocationsNumbered(num, locations);
  if (candidates.length === 0) return null;
  const point = lookup(geocodeQuery(addrText, cityZip));
  if (!point) return null;
  // An estimate is Google placing the number along a street, and in Miami's
  // grid that number repeats on every parallel street a block apart: "522 E
  // 24th St" can land as close to the 522 E 25th St clinic as a real misspelling
  // of it. So an estimate also has to be on a street with the location's name
  // ("E Ponce De Leon" / "Ponce de Leon", "W 20th Ct" / "W 20th Ave"); "24" is
  // not "25". An exact hit needs no such check: 75 m is the building itself.
  const name = streetName(addrText);
  let best: { loc: KnownLocation; d: number } | null = null;
  for (const { loc, pin, lines } of candidates) {
    const d = metresBetween(point, pin);
    if (d > MAP_RADIUS_M[point.precision]) continue;
    if (point.precision === "estimate" && (!name || !lines.some((l) => streetName(l) === name))) continue;
    if (!best || d < best.d) best = { loc, d };
  }
  return best
    ? { matched: true, locationId: best.loc.id, locationName: best.loc.name, confidence: "map" }
    : null;
}

/** The address fields of one booking, as both endpoints are compared. */
export interface TripEndpoints {
  pickupAddress: string | null | undefined;
  pickupCityZip?: string | null;
  destinationAddress: string | null | undefined;
  destinationCityZip?: string | null;
  /** A custom return destination, when the trip returns somewhere other than
   *  the original pickup. A THIRD endpoint: a round trip that ends at a Mount
   *  Sinai building does touch the hospital even if neither of the first two
   *  addresses does. */
  returnCustomDestinationAddress?: string | null;
  returnCustomDestinationCityZip?: string | null;
  /** Extra legs on a multi-leg trip, stored as JSONB. Only the address fields
   *  are read; anything else on the object is ignored. */
  additionalLegs?: ReadonlyArray<Record<string, unknown>> | null;
}

/** Pull the address-ish strings out of one `additional_legs` entry. The column
 *  is loosely-typed JSONB written by the booking form, so this reads
 *  defensively and ignores anything that is not a non-empty string. The form
 *  writes `from` / `to` (additionalLegSchema in trip.ts); only those were
 *  missing, so a stop at a Mount Sinai building mid-route went unseen. */
function legAddresses(leg: Record<string, unknown>): string[] {
  const keys = [
    "from",
    "to",
    "pickupAddress",
    "destinationAddress",
    "address",
    "destination",
  ];
  return keys
    .map((k) => leg[k])
    .filter((v): v is string => typeof v === "string" && v.trim().length > 0);
}

/** The return destination and extra legs: endpoints beyond the first two. */
export function extraEndpoints(trip: TripEndpoints): string[] {
  return [
    String(trip.returnCustomDestinationAddress ?? ""),
    ...(trip.additionalLegs ?? []).flatMap((leg) =>
      leg && typeof leg === "object" ? legAddresses(leg) : [],
    ),
  ].filter((a) => a.trim().length > 0);
}

/**
 * The geocode queries the map check would ask about for this trip: only for a
 * trip the wording alone reports as off-site, and only for its endpoints that
 * share a building number with a pinned location. Everything else is never
 * looked up — which keeps patients' home addresses away from Google unless one
 * happens to share a number with a Mount Sinai building.
 */
export function mapLookupsNeeded(
  trip: TripEndpoints,
  locations: readonly KnownLocation[],
): string[] {
  if (classifyTripEndpoints(trip, locations).verdict !== "neither_known") return [];
  return mapLookupsFor(trip, locations);
}

/** Whether the map check would accept this Google answer for this query at
 *  all. The background fill keeps coordinates only when it would, so points
 *  that could never count (a home far from any Mount Sinai building) aren't
 *  stored. */
export function mapPointMatches(
  query: string,
  locations: readonly KnownLocation[],
  point: GeocodedPoint,
): boolean {
  const q = query.trim();
  const num = houseNumber(q);
  return num !== "" && mapMatch(q, "", num, locations, () => point) !== null;
}

/** `mapLookupsNeeded` for a trip the caller already knows the wording reports
 *  as off-site, without classifying it again. */
export function mapLookupsFor(
  trip: TripEndpoints,
  locations: readonly KnownLocation[],
): string[] {
  const endpoints: Array<[string | null | undefined, string | null | undefined]> = [
    [trip.pickupAddress, trip.pickupCityZip],
    [trip.destinationAddress, trip.destinationCityZip],
    ...extraEndpoints(trip).map((a): [string, string] => [a, ""]),
  ];
  const queries: string[] = [];
  for (const [address, cityZip] of endpoints) {
    const num = houseNumber(address);
    if (!num || pinnedLocationsNumbered(num, locations).length === 0) continue;
    queries.push(geocodeQuery(address, cityZip));
  }
  return queries;
}

export interface TripLocationClassification {
  pickup: EndpointMatch;
  dropoff: EndpointMatch;
  verdict: TripLocationVerdict;
}

/**
 * Classify a booking's two endpoints against the known MSMC locations.
 *
 * Reports each end separately and derives a verdict; the CALLER decides which
 * verdicts warrant review. See `TripLocationVerdict` for why `one_known` is the
 * normal case and `neither_known` is the one worth a human's time.
 *
 * `indeterminate` is returned when either end has no usable address text, so a
 * missing field is never presented to a reviewer as a suspicious trip.
 */
export function classifyTripEndpoints(
  trip: TripEndpoints,
  locations: readonly KnownLocation[],
  lookup?: GeocodeLookup,
): TripLocationClassification {
  const pickup = matchAddressToLocations(
    trip.pickupAddress,
    trip.pickupCityZip,
    locations,
    lookup,
  );
  const dropoff = matchAddressToLocations(
    trip.destinationAddress,
    trip.destinationCityZip,
    locations,
    lookup,
  );

  const haveBothAddresses =
    String(trip.pickupAddress ?? "").trim().length > 0 &&
    String(trip.destinationAddress ?? "").trim().length > 0;

  // Every OTHER endpoint the itinerary touches. A round trip returning to a
  // Mount Sinai building, or a multi-leg trip that calls at one mid-route, does
  // reach the hospital — reporting it as "never touches Mount Sinai" because
  // the first two columns happened not to match would be a false positive on a
  // legitimate trip.
  const extraAddresses = extraEndpoints(trip);
  const anyExtraMatched = extraAddresses.some(
    (a) => matchAddressToLocations(a, "", locations, lookup).matched,
  );

  // An endpoint the matcher cannot PARSE is unknown, not off-site. Without
  // this, a place name with no house number that is also not a location label
  // ("Mount Sinai Medical Center") would push an ordinary trip onto the review
  // list — the failure this module's precision-over-recall rule exists to
  // avoid. Absence of evidence is reported as `indeterminate`.
  const parseable = (addr: string | null | undefined): boolean => {
    const t = String(addr ?? "").trim();
    if (!t) return false;
    return houseNumber(t).length > 0;
  };
  const bothEndpointsParseable =
    parseable(trip.pickupAddress) && parseable(trip.destinationAddress);

  let verdict: TripLocationVerdict;
  if (!haveBothAddresses) {
    verdict = "indeterminate";
  } else if (pickup.matched && dropoff.matched) {
    verdict = "both_known";
  } else if (pickup.matched || dropoff.matched || anyExtraMatched) {
    verdict = "one_known";
  } else if (!bothEndpointsParseable) {
    // Neither end matched, but at least one could not be read at all — so we
    // do not KNOW it is off-site. Say so rather than accuse.
    verdict = "indeterminate";
  } else {
    verdict = "neither_known";
  }

  return { pickup, dropoff, verdict };
}
